import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Highlight, connectRefinementList } from 'react-instantsearch-dom';

import refinementMap from './utils/refinement-map.json';

import { List, Accordion, AccordionTrigger, AccordionPanel } from './left-rail-shared';
import InputCheckbox from '../../atoms/checkbox';
import InputSearch from '../../atoms/input-search';
import PerfectScrollbarStyles from '../../theme/perfect-scrollbar';
import { logAmplitude } from '../../utils/amplitude';
import GA4 from '../../utils/ga4DataLayer';
import { sortAlgoliaItemsAlphaNumerically, sortShoes, sortAges, refineOrRedirect } from './utils/refinementHelpers';

const SearchRefinement = styled(InputSearch)`
  outline: 0;
  background-color: transparent;
  color: ${(props) => props.theme.color.brand};
  border: 1px solid ${(props) => props.theme.color.brand};
  margin-bottom: 2rem;
  ${(props) => props.theme.search(props.theme.color.brand, '12px center', 12)}

  ::placeholder {
    color: ${(props) => props.theme.color.brand};
    opacity: 0.8;
  }
  ::-moz-placeholder {
    color: ${(props) => props.theme.color.brand};
    opacity: 0.8;
  }
  ::-webkit-input-placeholder {
    color: ${(props) => props.theme.color.brand};
    opacity: 0.8;
  }
`;

const Checkbox = styled(InputCheckbox)(({ theme }) => ({
  color: theme.color.bluePrimary,
  fontSize: theme.modularScale.sixteen,
  lineHeight: '24px',
  '&::before': { borderColor: theme.color.bluePrimary },
  '&::after': {
    background: theme.color.bluePrimary,
    top: '15px',
    left: '-2px',
    width: '1.3em'
  },
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    color: theme.color.brand,
    '&::before': { borderColor: theme.color.brand },
    '&::after': { background: theme.color.brand }
  }
}));

const ListWrapper = styled.div`
  margin: 0 0 2rem 0;
  overflow: hidden;
  ${PerfectScrollbarStyles}
  li {
    label {
      text-align: left;
      margin-right: 1rem;
    }

    button,
    a {
      // allows for inner labels to stretch
      // entire width of wrapper
      width: 100%;
    }
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  > span:last-of-type {
    // force item label to wrap without
    // getting too close to item count
    margin-left: 1rem;
    color: ${({ theme }) => theme.color.brandLightBlue};
  }
`;

const ShowAll = styled.span(({ theme }) => ({
  color: theme.color.bluePrimary,
  textDecoration: 'underline',
  cursor: 'pointer',
  display: 'block',
  fontFamily: theme.font.sans,
  fontSize: theme.modularScale.sixteen,
  lineHeight: '18px',
  padding: '0.5rem 0 0.5rem 3.1rem',
  transition: `color ${theme.animation.default} ${theme.animation.easeOutQuad}`,
  ':hover': { color: theme.color.brand },
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    paddingLeft: '3.2rem'
  }
}));

const ShowMoreListItem = styled.li(({ showMore }) => ({
  display: showMore ? 'none' : undefined,
  marginTop: '0.6rem'
}));

const NoResults = styled.section(({ theme }) => ({
  alignItems: 'center',
  background: '#2B479814',
  color: theme.color.brand,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '1rem',
  fontFamily: theme.font.sans,
  fontSize: theme.modularScale.fourteen,
  lineHeight: '18px'
}));

const NoHyperlink = styled.button`
  border: none;
  background: none;
  padding: 0;
  // letter spacing to match global anchor tag style
  letter-spacing: 0.05rem;
`;

// switches between a button and a link to select filter for SEO purposes
// see ./utils/hyperlinkRules/index.js for details
const RefinementTrigger = ({ useHyperLink, children, ...props }) =>
  useHyperLink ? <a {...props}>{children}</a> : <NoHyperlink {...props}>{children}</NoHyperlink>;

RefinementTrigger.defaultProps = {
  useHyperLink: false
};

RefinementTrigger.propTypes = {
  useHyperLink: PropTypes.bool,
  children: PropTypes.node.isRequired
};

const RefinementList = connectRefinementList(
  ({
    attribute,
    createURL,
    isFromSearch,
    items,
    refine,
    searchable,
    searchForItems,
    showMore,
    toggleMore,
    useHyperlinks,
    itemsForHyperlinkCheck
  }) => {
    // exit early if component has no items to return;
    // excluding searchable attributes, like brand,
    // to allow for input search No Results functionality
    if (!searchable && items?.length === 0) return null;

    const router = useRouter();
    const ref = useRef();
    const psRef = useRef();
    const [active, toggle] = useState(false);
    const isMobileDevice = useSelector((state) => state.interfaces.isMobileDevice);

    const onFacetClick = (e, item) => {
      e.preventDefault();
      const facets = router.query;
      const navigationItem = `${refinementMap[attribute].id}:${item.label.toLowerCase()}`;
      const selected = !item.isRefined;

      logAmplitude('Clicked Facet', {
        navigationItem,
        filterAction: 'Filter Applied',
        selected,
        facets
      });

      // Track GA4 facet click
      GA4.clickFacet({
        facet_name: refinementMap[attribute].label,
        facet_value: item.label,
        facet_type: attribute,
        page_location: router.asPath
      });

      const path = router.asPath;
      if (path.includes("outlet/kids") && selected === false) {
        refineOrRedirect(item, facets, router, refine);
      } else {
        refine(item.value);
      }
    };

    const onTriggerClick = () => {
      const facets = router.query;
      const navigationItem = refinementMap[attribute].id;
      const selected = !active;

      logAmplitude('Clicked Facet', {
        navigationItem,
        filterAction: 'Filter Clicked',
        selected,
        facets
      });

      // Track GA4 facet click
      GA4.clickFacet({
        facet_name: refinementMap[attribute].label,
        facet_value: 'toggle',
        facet_type: attribute,
        page_location: router.asPath
      });

      toggle(!active);
      setTimeout(() => toggleMore(false), 0);
    };

    useEffect(() => {
      const listener = (e) => {
        const { current: elem } = ref;
        // If the refinement item that was clicked was a hyperlink, it will be changed
        // to a button, so we need to search again for the element by the ID of the
        // original target inside of the original accordion to keep the accordion 'open'
        const accordion = global.document.getElementById(`${attribute}-accordion`);
        const target = global.document.getElementById(e.target.id);
        // this handles components that accept an ID.
        if (target && !accordion?.contains(target)) onTriggerClick();
        // Algolia components like Highlight do not accept an ID and need to be handled differently.
        else if (!elem.contains(e.target)) onTriggerClick();
      };

      if (active) global.document.addEventListener('click', listener);
      else global.document.removeEventListener('click', listener);

      return () => global.document.removeEventListener('click', listener);
    }, [active, useHyperlinks, itemsForHyperlinkCheck]);

    useEffect(() => {
      if (psRef.current) psRef.current.updateScroll();
    }, [psRef]);

    return (
      <Accordion id={`${attribute}-accordion`} ref={ref}>
        <AccordionTrigger isOpened={active} aria-expanded={active} onClick={onTriggerClick}>
          {refinementMap[attribute].label}
        </AccordionTrigger>

        <AccordionPanel active={active}>
          {searchable && (
            <SearchRefinement
              id={`${attribute}.search-input`}
              type="input"
              placeholder={`Search for a ${attribute}...`}
              onChange={(event) => searchForItems(event.currentTarget.value)}
            />
          )}

          <ListWrapper>
            {items.length > 0 ? (
              <List component="ul" ref={psRef} showMore={showMore} className="ps">
                {attribute === 'on_sale'
                  ? items
                      .filter(item => item.label === 'true')
                      .map((item) => (
                        <li key={item.label}>
                          <RefinementTrigger
                            useHyperLink={false}
                            aria-label={`${item.isRefined ? 'remove' : 'add'} filter, On Sale`}
                            onClick={e => onFacetClick(e, item)}
                          >
                            <Checkbox
                              labelID="on_sale.label"
                              id="on_sale"
                              name="on_sale"
                              value="on_sale"
                              active={item.isRefined}
                            >
                              <LabelWrapper>
                                <span>On Sale</span>
                              </LabelWrapper>
                            </Checkbox>
                          </RefinementTrigger>
                        </li>
                      ))
                  : items.map((item) => {
                      const renderAsHyperlink =
                        useHyperlinks ||
                        itemsForHyperlinkCheck?.[item?.value?.[0]] ||
                        (item.isRefined && itemsForHyperlinkCheck?.[item?.label]);
                      return (
                        <li key={item.label}>
                          <RefinementTrigger
                            useHyperLink={renderAsHyperlink}
                            aria-label={`${item.isRefined ? 'remove' : 'add'} filter, ${refinementMap[attribute].label} ${item.label
                            }`}
                            href={renderAsHyperlink ? createURL(item.value) : undefined}
                            onClick={(e) => onFacetClick(e, item)}>
                            <Checkbox
                              labelID={`${attribute}.${item.label}.label`}
                              id={`${attribute}.${item.label}`}
                              name={item.label}
                              value={item.label}
                              active={item.isRefined}>
                              <LabelWrapper>
                                {isFromSearch ? (
                                  <Highlight attribute="label" tagName="mark" hit={item} />
                                ) : (
                                  <span>{item.label}</span>
                                )}
                                <span>{item.count}</span>
                              </LabelWrapper>
                            </Checkbox>
                          </RefinementTrigger>
                        </li>
                      );
                    })}

                {items.length >= 10 && attribute !== "variants.age_range" && (
                  <ShowMoreListItem showMore={showMore}>
                    <ShowAll id={`${attribute}.show-all`} onClick={() => toggleMore(true)}>
                      {isMobileDevice ? 'Show all' : 'Show All...'}
                    </ShowAll>
                  </ShowMoreListItem>
                )}
              </List>
            ) : (
              <List component="div">
                <NoResults id={`${attribute}.no-results`}>No Results</NoResults>
              </List>
            )}
          </ListWrapper>
        </AccordionPanel>
      </Accordion>
    );
  }
);

const Refinement = (props) => {
  let sortRefinements;

  switch (props.attribute) {
    case 'variants.age_range':
      sortRefinements = sortAges;
      break;
    case 'variants.shoe_sizes':
      sortRefinements = sortShoes;
      break;
    // for discount percent ranges, we will be following the same order which wil be coming in Algolia response and will avoid sorting it in FE side
    case 'discount_percent_ranges':
      break;
    // TODO: comment back once clothing sizes are fixed
    // case 'variants.clothing_sizes':
    //   sortRefinements = sortClothingSizes;
    //   break;
    default:
      sortRefinements = sortAlgoliaItemsAlphaNumerically;
  }

  const [showMore, setShowMore] = useState(false);

  return (
    <RefinementList
      {...props}
      showMoreLimit={9999}
      showMore={showMore}
      toggleMore={setShowMore}
      transformItems={sortRefinements}
    />
  );
};

Refinement.propTypes = {
  attribute: PropTypes.string.isRequired
};

export default Refinement;
